<script setup lang="ts">
import { Store as StoreIcon } from 'lucide-vue-next'
import type { Placement } from '@popperjs/core'

interface Store {
  id: number
  name: string
}

const props = withDefaults(
  defineProps<{
    stores: Store[]
    availableStoreIds?: number[]
    detailExpandable?: boolean
    position?: Placement
  }>(),
  {
    detailExpandable: false,
    position: 'bottom'
  }
)

const { t } = useI18n()

const isDetailModalOpen = ref(false)
const searchText = ref('')

const sortedStores = computed(() => {
  return [...props.stores].sort((a) => {
    return !props.availableStoreIds || props.availableStoreIds?.includes(a.id) ? -1 : 1
  })
})

const filteredStores = computed(() => {
  return sortedStores.value.filter((store) => store.name.toLowerCase().includes(searchText.value.toLowerCase()))
})

const showDetailModal = () => {
  if (props.stores.length > 5 && props.detailExpandable) {
    isDetailModalOpen.value = true
  }
}

const handleCloseDetailModal = () => {
  isDetailModalOpen.value = false
  searchText.value = ''
}
</script>

<template>
  <div class="flex items-center">
    <AppTooltip v-if="stores.length > 0" :tooltip-position="position">
      <template #tooltip-content>
        <div class="flex flex-col gap-2 px-1 text-left">
          <span v-if="detailExpandable && stores.length > 5" class="opacity-50 text-xs italic">
            {{ t('global.multiselect.stores.clickToShowStores', { count: stores.length }) }}
          </span>

          <span
            v-for="store in sortedStores.slice(0, 5)"
            :key="store.id"
            :class="[
              'text-md',
              !availableStoreIds || availableStoreIds?.includes(store.id) ? 'opacity-100' : 'opacity-50'
            ]"
          >
            {{ store.name }}
          </span>

          <span v-if="stores.length > 5">
            {{ t('global.multiselect.stores.otherStores', { count: stores.length - 5 }) }}
          </span>
        </div>
      </template>
      <BaseChip
        :text="t('global.multiselect.stores.storesCount', { count: stores.length })"
        :clickable="detailExpandable && stores.length > 5"
        :class="[detailExpandable && stores.length > 5 ? 'cursor-pointer' : '']"
        @click="showDetailModal"
      >
        <template #leading>
          <StoreIcon />
        </template>
      </BaseChip>
    </AppTooltip>

    <div v-else>
      <BaseChip :text="t('global.multiselect.stores.noStore')" color="yellow">
        <template #leading>
          <StoreIcon />
        </template>
      </BaseChip>
    </div>
  </div>

  <BaseModal
    v-if="isDetailModalOpen"
    is-open
    :heading-title="t('global.multiselect.stores.totalStoresCount', { count: stores.length })"
    :show-footer="false"
    size="lg"
    class="!h-[500px] overflow-y-auto"
    @close="handleCloseDetailModal"
  >
    <div class="flex flex-col gap-4 w-full">
      <div class="relative w-full">
        <Input
          v-model="searchText"
          type="search"
          class="pl-10"
          :placeholder="t('global.multiselect.stores.searchPlaceholder')"
        />
        <BaseIcon
          name="HeroMagnifyingGlassIconOutline"
          class="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-foreground"
        />
      </div>

      <div class="w-full flex flex-wrap gap-2">
        <BaseChip
          v-for="store in filteredStores"
          :key="store.id"
          :text="store.name"
          :color="!availableStoreIds || availableStoreIds?.includes(store.id) ? 'green' : 'gray'"
        />
      </div>

      <div v-if="filteredStores.length === 0" class="w-full flex justify-center">
        <span class="text-sm text-foreground-secondary">
          {{ t('global.multiselect.stores.noResultSearch') }}
        </span>
      </div>
    </div>
  </BaseModal>
</template>
